/** Shared error helpers for the public expert-claims endpoints. */

interface ApiErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: unknown;
      error?: unknown;
      errors?: unknown;
      data?: { can_resend?: unknown };
    };
  };
}

const LARAVEL_DEFAULT_THROTTLE_MESSAGE = 'Too Many Attempts.';

export const FALLBACK_THROTTLE_MESSAGE = 'Too many attempts — please try again in a minute.';

export function extractStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  return (error as ApiErrorLike).response?.status;
}

/** Repo convention: response message → response error → thrown Error message. */
export function extractServerMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const candidate = error as ApiErrorLike;
  const data = candidate.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof candidate.message === 'string' && candidate.message.trim()) return candidate.message.trim();
  return '';
}

/**
 * The claim endpoints throttle twice: the 60s per-claim resend throttle returns
 * a friendly JSON message ("Please wait before requesting another email."),
 * while the route-level `throttle:10,1` returns Laravel's default body
 * ("Too Many Attempts." — or no JSON message at all). Show the server's message
 * when it is a real one; replace the Laravel default with friendlier copy.
 */
export function resolveThrottleMessage(error: unknown): string {
  const message = extractServerMessage(error);
  if (message && message !== LARAVEL_DEFAULT_THROTTLE_MESSAGE) return message;
  return FALLBACK_THROTTLE_MESSAGE;
}

/** Laravel 422 `errors` bag → one message per field for UiForm `serverErrors`. */
export function mapValidationErrors(error: unknown): Record<string, string> {
  const errors = (error as ApiErrorLike)?.response?.data?.errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return {};

  const mapped: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    if (typeof messages === 'string' && messages.trim()) {
      mapped[field] = messages.trim();
    } else if (Array.isArray(messages) && typeof messages[0] === 'string' && messages[0].trim()) {
      mapped[field] = messages[0].trim();
    }
  }
  return mapped;
}
